import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import OrganizationService from "@/services/organizationService";

import LoginService from "../../services/loginService";
import { getItem } from "../../utils/local_storage";

const initialState = {
  loginDetails: {
    loggedIn: false,
    accessToken: "",
    refreshToken: "",
    error: false,
    altmessage: "",
    orgDetails: {},
    midOrgs: [],
  },
  loading: false,
};

export const verifyUser = createAsyncThunk("user/verifyUser", async ({ accountDetails, email, handleToast }, { rejectWithValue }) => {
  try {
    const res = await LoginService.verifyUser({ accountDetails, email });
    return res?.data;
  } catch (e) {
    setTimeout(() => {
      handleToast(e?.response?.data?.error?.message || "Something went wrong", "error");
      setTimeout(() => {
        window.location.reload();
      }, 4000);
    }, 0);
    return rejectWithValue(e?.response?.data);
  }
});

export const check2FA = createAsyncThunk("user/check2FA", async (data, { rejectWithValue }) => {
  try {
    const res = await LoginService.check2FA(data);
    return res?.data;
  } catch (e) {
    e.response.data = {
      ...e?.response?.data,
      status: e?.response?.status,
    };
    return rejectWithValue(e?.response?.data);
  }
});

export const singleSignOn = createAsyncThunk("user/verifyUser", async ({ token, setCustomer = false, product }, { rejectWithValue }) => {
  try {
    const res = await LoginService.verifyCustomer({ token, setCustomer, product });
    return res?.data;
  } catch (e) {
    e.response.data = {
      ...e?.response?.data,
      status: e?.response?.status,
    };
    return rejectWithValue(e?.response?.data);
  }
});

export const verifyToken = createAsyncThunk("user/verifyToken", async ({ token, setCustomer = false }, { rejectWithValue }) => {
  try {
    const res = await LoginService.verifyToken({ redirectToken: token, setCustomer });
    return res?.data;
  } catch (e) {
    e.response.data = {
      ...e?.response?.data,
      status: e?.response?.status,
    };
    return rejectWithValue(e?.response?.data);
  }
});

export const googleLogin = createAsyncThunk("user/verifyUser", async ({ token }, { rejectWithValue }) => {
  try {
    const res = await LoginService.googleLogin({ token });
    return res?.data;
  } catch (e) {
    e.response.data = {
      ...e?.response?.data,
      status: e?.response?.status,
    };
    return rejectWithValue(e?.response?.data);
  }
});

export const getUser = createAsyncThunk("user/getUser", async (body, { rejectWithValue }) => {
  try {
    const res = await LoginService.getUser();
    return res?.data;
  } catch (e) {
    return rejectWithValue(e?.response?.data);
  }
});

export const createOrganization = createAsyncThunk("user/createOrganization", async (body, { rejectWithValue }) => {
  try {
    const res = await LoginService.createOrganization(body);
    return res?.data;
  } catch (e) {
    return rejectWithValue(e?.response?.data);
  }
});

export const verifyAndCreateCustomer = createAsyncThunk("user/verifyAndCreateCustomer", async (body, { rejectWithValue }) => {
  try {
    const res = await LoginService.verifyAndCreateCustomer(body);
    return res?.data;
  } catch (e) {
    return rejectWithValue(e?.response?.data);
  }
});

export const signOutUser = createAsyncThunk("user/signOutUser", async () => {
  localStorage.removeItem("loggedIn");
  localStorage.removeItem("access-token");
  localStorage.removeItem("refresh-token");
  localStorage.removeItem("country");
  localStorage.removeItem("countryCode");
  window.sessionStorage.clear();
  localStorage.clear();
  return true;
});

export const fetchSubOrganizations = createAsyncThunk("organizations/fetchSubOrganizations", async (_, { rejectWithValue }) => {
  try {
    const response = await OrganizationService.getSubOrgs();
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    resetDetails: (state) => {
      state.loginDetails.altmessage = "";
    },
    updateOrgDetails: (state, action) => {
      const { orgId } = action.payload;
      return {
        ...state,
        orgDetails: {
          ...state.orgDetails,
          orgId: orgId || state.loginDetails.orgDetails.orgId,
        },
      };
    },
  },
  extraReducers: {
    [verifyUser.pending]: (state, action) => {
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
        },
        loading: true,
      };
      return newState;
    },
    [verifyUser.fulfilled]: (state, action) => {
      const { status, accessToken, refreshToken, cxId = "", message } = action?.payload?.data || {};
      if (status === "verified") {
        localStorage.setItem("access-token", accessToken);
        localStorage.setItem("refresh-token", refreshToken);
        localStorage.setItem("loggedIn", true);

        state["loginDetails"] = {
          loggedIn: true,
          accessToken,
          refreshToken,
          created: false,
        };
      }
      if (status === "created") {
        state["loginDetails"] = {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
          created: true,
          message,
          cxId: cxId,
        };
      }
      state["loading"] = false;
    },
    [verifyUser.rejected]: (state, action) => {
      localStorage.removeItem("access-token");
      localStorage.removeItem("refresh-token");
      localStorage.removeItem("loggedIn");
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: action.payload?.error?.message,
        },
        loading: false,
      };
      return newState;
    },
    [getUser.pending]: (state, action) => {
      state["userDetails"] = {
        loading: true,
      };
    },
    [getUser.fulfilled]: (state, action) => {
      const { status, customerDetails, orgDetails, environment } = action?.payload?.data || {};
      if (status) {
        state["userDetails"] = {
          userStatus: status,
          ...customerDetails,
        };
        state["orgDetails"] = orgDetails;
        state["env"] = environment;
        state["loginDetails"] = {
          loggedIn: true,
          accessToken: getItem("access-token"),
          refreshToken: getItem("refresh-token"),
        };
      }
    },
    [getUser.rejected]: (state, action) => {
      localStorage.removeItem("access-token");
      localStorage.removeItem("refresh-token");
      localStorage.removeItem("loggedIn");
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: action.payload?.error?.message,
        },
        loading: false,
      };
      return newState;
    },
    [signOutUser.fulfilled]: (state, action) => {
      return initialState;
    },
    [createOrganization.pending]: (state, action) => {
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          created: true,
          error: false,
          alterror: false,
        },
        loading: true,
      };
      return newState;
    },
    [createOrganization.fulfilled]: (state, action) => {
      const { status, message, companyDetails = {}, cxId } = action?.payload?.data || {};
      if (status === "created") {
        state["loginDetails"] = {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
          created: false,
          alterror: false,
          altmessage: message,
        };
      }
      if (status === "exists") {
        state["loginDetails"] = {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
          created: false,
          alterror: false,
          altmessage: message,
          exist: true,
          orgDetails: companyDetails,
          cxId: cxId,
        };
      }
      state["loading"] = false;
    },
    [createOrganization.rejected]: (state, action) => {
      const { message } = action?.payload?.error || { message: "Some Internal Error Occured" };
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          created: false,
          alterror: message,
          altmessage: message,
        },
        loading: false,
      };
      return newState;
    },
    [verifyAndCreateCustomer.pending]: (state, action) => {
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          created: false,
          error: false,
          alterror: false,
        },
        loading: true,
      };
      return newState;
    },
    [verifyAndCreateCustomer.fulfilled]: (state, action) => {
      const { status, message, cxId } = action?.payload?.data || {};
      if (status === "created") {
        state["loginDetails"] = {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
          created: true,
          message,
          cxId: cxId,
        };
      }
      state["loading"] = false;
    },
    [verifyAndCreateCustomer.rejected]: (state, action) => {
      const { message } = action?.payload?.error || { message: "Some Internal Error Occured" };
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          created: false,
          alterror: message,
          altmessage: message,
        },
        loading: false,
      };
      return newState;
    },
    [check2FA.pending]: (state, action) => {
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
        },
        loading: true,
      };
      return newState;
    },
    [check2FA.fulfilled]: (state, action) => {
      const { status, cxId = "", message, mfaStatus = false } = action?.payload?.data || {};
      if (status === "created") {
        state["loginDetails"] = {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
          created: true,
          message,
          cxId: cxId,
        };
      } else {
        state["loginDetails"] = {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
          message,
          mfaStatus,
        };
      }
      state["loading"] = false;
    },
    [check2FA.rejected]: (state, action) => {
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: action.payload?.error?.message,
        },
        loading: false,
      };
      return newState;
    },
    [verifyToken.pending]: (state, action) => {
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: false,
        },
        loading: true,
      };
      return newState;
    },
    [verifyToken.fulfilled]: (state, action) => {
      const { status, accessToken, refreshToken, cxId = "" } = action?.payload?.data || {};
      if (status === "verified") {
        if (accessToken && refreshToken) {
          localStorage.setItem("access-token", accessToken);
          localStorage.setItem("refresh-token", refreshToken);
          localStorage.setItem("loggedIn", true);
        }

        state["loginDetails"] = {
          loggedIn: true,
          accessToken,
          refreshToken,
          created: true,
          cxId: cxId,
        };
      }
      state["loading"] = false;
    },
    [verifyToken.rejected]: (state, action) => {
      let newState = {
        loginDetails: {
          loggedIn: false,
          accessToken: "",
          refreshToken: "",
          error: action.payload?.error?.message,
        },
        loading: false,
      };
      return newState;
    },
    [fetchSubOrganizations.pending]: (state) => {
      state.loading = true;
    },
    [fetchSubOrganizations.fulfilled]: (state, action) => {
      const { data } = action?.payload?.data || {};
      if (data) {
        state.midOrgs = [...data];
      }
    },
    [fetchSubOrganizations.rejected]: (state) => {
      let newState = {
        ...state,
        loginDetails: {
          midOrgs: [],
        },
        loading: false,
      };
      return newState;
    },
  },
});

export const { resetDetails, updateOrgDetails } = loginSlice.actions;

const { reducer: loginReducer } = loginSlice;
export default loginReducer;
